/**
 * InsuranceModal — clone of LoanModal's structure adapted for insurance
 * policies. Editable fields + 8 attachment sections (7 versioned + 1 flat).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Paperclip, Eye, Download, Trash, FloppyDisk, X } from "@phosphor-icons/react";
import api, { apiErrorMessage } from "@/lib/api";
import ChangeLogNotes from "@/components/shared/ChangeLogNotes";
import { TYPO_COLS, findTypoMatch, TypoWarningDialog } from "./TypoWarning";

const ATTACHMENT_SECTIONS = [
  { id: "quote",                       label: "Quote" },
  { id: "binder",                      label: "Binder" },
  { id: "dec_page_renewal",            label: "Dec Page / Renewal" },
  { id: "invoices",                    label: "Invoices" },
  { id: "financing_agreements",        label: "Financing Agreements" },
  { id: "audit_documents",             label: "Audit Documents" },
  { id: "internal_document_versions",  label: "Internal Document Versions" },
  { id: "additional_documents",        label: "Additional Documents", versioned: false },
];

const LABELS = { insured_asset: "Insured Asset", insured_entity: "Insured Entity", type: "Type", category: "Category", type_code: "Type Code",
  carrier: "Carrier", broker: "Broker", broker_phone: "Broker Phone", broker_email: "Broker Email" };

export default function InsuranceModal({ policy, customCols, onClose, onChange, suggestions = {} }) {
  const [typoPrompts, setTypoPrompts] = useState(null);
  const [form, setForm] = useState(null);
  const [attachments, setAttachments] = useState({});
  const [uploading, setUploading] = useState("");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const fileInputRefs = useRef({});

  useEffect(() => { setForm(policy ? { ...policy } : null); setDirty(false); }, [policy]);

  const loadAtt = useCallback(async (pid) => {
    try {
      const { data } = await api.get(`/insurance/${pid}/attachments`);
      setAttachments(data || {});
    } catch (e) { /* ignore */ }
  }, []);
  useEffect(() => { if (policy?.id) loadAtt(policy.id); else setAttachments({}); }, [policy?.id, loadAtt]);

  if (!policy || !form) return null;
  const setField = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setDirty(true); };

  // Only fields the user changed are checked, so legacy spellings already on the policy don't nag on every save.
  const save = () => {
    const entries = [...TYPO_COLS].filter((k) => form[k] && form[k] !== policy[k])
      .map((k) => ({ col: k, label: LABELS[k], input: form[k], suggestion: findTypoMatch(form[k], (suggestions[k] || []).filter((o) => o !== form[k])) }))
      .filter((e) => e.suggestion);
    if (!entries.length) return persist(form);
    setTypoPrompts({
      entries,
      onAcceptAll: () => { const fixed = { ...form }; entries.forEach((e) => { fixed[e.col] = e.suggestion; }); setForm(fixed); setTypoPrompts(null); persist(fixed); },
      onProceed: () => { setTypoPrompts(null); persist(form); },
    });
  };

  const persist = async (src) => {
    setSaving(true);
    try {
      const payload = { ...src };
      delete payload.id; delete payload.insurance_id; delete payload.created_at;
      delete payload.updated_at; delete payload.created_by_id; delete payload.created_by_name;
      delete payload.updated_by_id; delete payload.updated_by_name; delete payload.has_attachments;
      const { data } = await api.put(`/insurance/${policy.id}`, payload);
      onChange && onChange(data);
      setDirty(false);
      toast.success("Policy saved");
    } catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
    finally { setSaving(false); }
  };

  const upload = async (section, file) => {
    if (!file) return;
    setUploading(section);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/insurance/${policy.id}/attachments?section=${section}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded to ${section.replace(/_/g, " ")}`);
      await loadAtt(policy.id);
    } catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
    finally { setUploading(""); }
  };

  const remove = async (fid) => {
    try {
      await api.delete(`/insurance/${policy.id}/attachments/${fid}`);
      await loadAtt(policy.id);
    } catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
  };

  const del = async () => {
    if (!window.confirm(`Delete policy #${policy.insurance_id}? It will be archived and can be restored later.`)) return;
    try {
      await api.delete(`/insurance/${policy.id}`);
      toast.success("Policy deleted");
      onClose();
    } catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
  };

  const backend = process.env.REACT_APP_BACKEND_URL;
  const attachUrl = (fid, download) => `${backend}/api/insurance/${policy.id}/attachments/${fid}${download ? "?download=true" : ""}`;

  return (
    <Dialog open={!!policy} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl" data-testid="insurance-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>Policy #{policy.insurance_id}{form.insured_asset ? ` · ${form.insured_asset}` : ""}</span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={del} className="text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="insurance-modal-delete">
                <Trash size={12} className="mr-1" /> Delete
              </Button>
              <Button size="sm" onClick={save} disabled={saving || !dirty} className="bg-[#1f64c2] hover:bg-[#1854a3]" data-testid="insurance-modal-save">
                <FloppyDisk size={12} className="mr-1" /> {saving ? "Saving…" : "Save"}
              </Button>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-4">
          <div className="space-y-4">
          {/* KPI header cards */}
          <ModalKpis form={form} />

          {/* Fields */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            <F label="Insured Asset" value={form.insured_asset} onChange={(v) => setField("insured_asset", v)} list={suggestions.insured_asset} listId="ins-modal-insured_asset" testId="insurance-modal-insured_asset" />
            <F label="Insured Entity" value={form.insured_entity} onChange={(v) => setField("insured_entity", v)} list={suggestions.insured_entity} listId="ins-modal-insured_entity" testId="insurance-entity" />
            <F label="Type" value={form.type} onChange={(v) => setField("type", v)} list={suggestions.type} listId="ins-modal-type" testId="insurance-modal-type" />
            <F label="Category" value={form.category} onChange={(v) => setField("category", v)} list={suggestions.category} listId="ins-modal-category" testId="insurance-modal-category" />
            <F label="Type Code" value={form.type_code} onChange={(v) => setField("type_code", v)} list={suggestions.type_code} listId="ins-modal-type_code" testId="insurance-modal-type_code" />
            <F label="Expiry Date" value={form.expiry_date} onChange={(v) => setField("expiry_date", v)} type="date" testId="insurance-expiry" />
            <F label="Start Date" value={form.start_date} onChange={(v) => setField("start_date", v)} type="date" />
            <F label="End Date" value={form.end_date} onChange={(v) => setField("end_date", v)} type="date" />
            <F label="Coverage Time (months)" value={form.coverage_time ?? ""} onChange={(v) => setField("coverage_time", v ? Number(v) : null)} type="number" step="0.1" />
            <F label="Premium Price ($)" value={form.premium_price ?? ""} onChange={(v) => setField("premium_price", v ? Number(v) : null)} type="number" testId="insurance-premium" />
            <F label="Policy Number" value={form.policy_number} onChange={(v) => setField("policy_number", v)} />
            <F label="Carrier" value={form.carrier} onChange={(v) => setField("carrier", v)} list={suggestions.carrier} listId="ins-modal-carrier" testId="insurance-modal-carrier" />
            <F label="Financing" value={form.financing} onChange={(v) => setField("financing", v)} />
            <F label="Broker" value={form.broker} onChange={(v) => setField("broker", v)} list={suggestions.broker} listId="ins-modal-broker" testId="insurance-modal-broker" />
            <F label="Broker Phone" value={form.broker_phone} onChange={(v) => setField("broker_phone", v)} list={suggestions.broker_phone} listId="ins-modal-broker_phone" testId="insurance-modal-broker_phone" type="tel" />
            <F label="Broker Email" value={form.broker_email} onChange={(v) => setField("broker_email", v)} list={suggestions.broker_email} listId="ins-modal-broker_email" testId="insurance-modal-broker_email" type="email" />
          </div>

          {/* Custom columns — editable, values live under form.custom_fields */}
          {Array.isArray(customCols) && customCols.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">Custom fields</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="insurance-custom-fields">
                {customCols.map((c) => (
                  <F
                    key={c.id}
                    label={c.label}
                    value={(form.custom_fields || {})[c.key] ?? ""}
                    onChange={(v) => setField("custom_fields", {
                      ...(form.custom_fields || {}),
                      [c.key]: (c.type === "text" || c.type === "date") ? (v || "") : (v === "" || v == null ? null : Number(v)),
                    })}
                    type={
                      c.type === "date" ? "date" :
                      (c.type === "number" || c.type === "money" || c.type === "percent") ? "number" :
                      "text"
                    }
                    step={c.type === "percent" ? "0.001" : "any"}
                    testId={`insurance-custom-${c.key}`}
                  />
                ))}
              </div>
            </div>
          )}

          <label className="block text-[10px]">
            <span className="uppercase tracking-widest text-slate-500 font-semibold">Full Address</span>
            <Textarea rows={2} value={form.full_address || ""} onChange={(e) => setField("full_address", e.target.value)} className="text-xs mt-0.5" data-testid="insurance-address" />
          </label>

          {/* Attachments */}
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mt-2 mb-2">Documents</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {ATTACHMENT_SECTIONS.map((s) => {
                const versions = attachments[s.id] || [];
                const current = versions[0];
                const previous = versions.slice(1);
                const isFlat = s.versioned === false;
                return (
                  <div key={s.id} className="rounded-md border border-slate-200 bg-slate-50/50 p-2" data-testid={`insurance-attach-section-${s.id}`}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="text-[11px] font-bold text-slate-700">{s.label}</div>
                      <button type="button"
                              onClick={() => fileInputRefs.current[s.id]?.click()}
                              disabled={uploading === s.id}
                              className="text-[10px] font-semibold text-[#1f64c2] hover:text-[#327de3] inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-slate-200 bg-white hover:border-[#327de3]"
                              data-testid={`insurance-attach-upload-${s.id}`}>
                        <Paperclip size={10} /> {uploading === s.id ? "Uploading…" : "Add file"}
                      </button>
                      <input ref={(el) => { fileInputRefs.current[s.id] = el; }} type="file" className="hidden"
                             onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(s.id, f); e.target.value = ""; }} />
                    </div>
                    {isFlat ? (
                      versions.length === 0 ? (
                        <div className="text-[10px] text-slate-400 italic">No files uploaded.</div>
                      ) : (
                        <div className="space-y-0.5">
                          {versions.map((v) => <Row key={v.id} v={v} attachUrl={attachUrl} onDelete={() => remove(v.id)} hideVersion />)}
                        </div>
                      )
                    ) : !current ? (
                      <div className="text-[10px] text-slate-400 italic">No files uploaded.</div>
                    ) : (
                      <>
                        <div className="text-[9px] font-bold uppercase tracking-widest text-emerald-700 mb-0.5">Current version</div>
                        <Row v={current} attachUrl={attachUrl} onDelete={() => remove(current.id)} />
                        {previous.length > 0 && (
                          <details className="mt-1">
                            <summary className="text-[9px] uppercase tracking-widest font-semibold text-slate-500 cursor-pointer">Previous versions ({previous.length})</summary>
                            <div className="mt-1 space-y-0.5 pl-3 border-l border-slate-200">
                              {previous.map((v) => <Row key={v.id} v={v} attachUrl={attachUrl} onDelete={() => remove(v.id)} />)}
                            </div>
                          </details>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          </div>
          {/* RIGHT: change log + notes */}
          <ChangeLogNotes basePath={`/insurance/${policy.id}`} idFieldLabel="policy" />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}><X size={13} className="mr-1" /> Close</Button>
        </DialogFooter>
        <TypoWarningDialog prompts={typoPrompts} onCancel={() => setTypoPrompts(null)} />
      </DialogContent>
    </Dialog>
  );
}

function F({ label, value, onChange, type, testId, step, list, listId }) {
  const id = list?.length ? listId : undefined;
  return (
    <label className="block text-[10px]">
      <span className="uppercase tracking-widest text-slate-500 font-semibold">{label}</span>
      <Input type={type || "text"} step={step} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="text-xs h-7 mt-0.5" data-testid={testId} list={id} autoComplete="off" />
      {id && <datalist id={id} data-testid={`${listId}-options`}>{list.map((o) => <option key={o} value={o} />)}</datalist>}
    </label>
  );
}

function _fmtMoney(n) {
  if (n == null || n === "" || Number.isNaN(Number(n))) return "—";
  return `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}
function _fmtDateMDY(s) {
  if (!s) return "—";
  const d = new Date(String(s).length <= 10 ? String(s) + "T00:00:00" : String(s));
  if (Number.isNaN(d.getTime())) return "—";
  return `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}/${d.getFullYear()}`;
}
function _coverageMonths(p) {
  const explicit = Number(p.coverage_time);
  if (Number.isFinite(explicit) && explicit > 0) return explicit;
  if (p.start_date && p.end_date) {
    const s = new Date(p.start_date + "T00:00:00");
    const e = new Date(p.end_date + "T00:00:00");
    if (!Number.isNaN(s) && !Number.isNaN(e) && e > s) {
      return Math.max(0.1, (e - s) / (1000 * 60 * 60 * 24 * 30.4375));
    }
  }
  return null;
}

function ModalKpis({ form }) {
  const months = _coverageMonths(form);
  const price = Number(form.premium_price);
  const monthlyEq = (Number.isFinite(price) && months && months > 0) ? price / months : null;
  const cards = [
    { label: "Insured Asset",   value: form.insured_asset  || "—", testId: "mkpi-asset" },
    { label: "Insured Entity",  value: form.insured_entity || "—", testId: "mkpi-entity" },
    { label: "Insurance Type",  value: form.type           || "—", testId: "mkpi-type" },
    { label: "Expiration Date", value: _fmtDateMDY(form.expiry_date), testId: "mkpi-expiry" },
    { label: "Premium Price",   value: _fmtMoney(form.premium_price), testId: "mkpi-premium" },
    { label: "Coverage Time",   value: months ? `${Number(months).toFixed(months >= 1 ? 0 : 1)} mo` : "—", testId: "mkpi-coverage" },
    { label: "Monthly Equivalent Premium", value: _fmtMoney(monthlyEq), highlight: true, testId: "mkpi-monthly-eq" },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-2" data-testid="insurance-modal-kpis">
      {cards.map((c) => (
        <div key={c.label}
             className={`rounded-lg border px-2.5 py-1.5 ${c.highlight ? "border-[#327de3] bg-[#eaf2ff]" : "border-slate-200 bg-white"}`}
             data-testid={c.testId}>
          <div className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold truncate" title={c.label}>{c.label}</div>
          <div className={`text-sm font-bold leading-tight mt-0.5 truncate ${c.highlight ? "text-[#1f64c2]" : "text-slate-900"}`} title={String(c.value)}>{c.value}</div>
        </div>
      ))}
    </div>
  );
}

function Row({ v, attachUrl, onDelete, hideVersion }) {
  return (
    <div className="flex items-center gap-1 text-[10px] bg-white border border-slate-200 rounded px-1.5 py-0.5">
      {!hideVersion && <span className="font-semibold text-slate-500">v{v.version}</span>}
      <span className="truncate flex-1" title={v.filename}>{v.filename}</span>
      <a href={attachUrl(v.id)} target="_blank" rel="noreferrer" className="text-[#1f64c2] hover:text-[#327de3]"><Eye size={10} /></a>
      <a href={attachUrl(v.id, true)} className="text-[#1f64c2] hover:text-[#327de3]"><Download size={10} /></a>
      <button onClick={onDelete} className="text-rose-500 hover:text-rose-700"><Trash size={10} /></button>
    </div>
  );
}
